import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  NgZone,
  input,
  output,
  OnInit,
  viewChild,
} from '@angular/core';
import { ConfigService, GenericGroupResult, OrdersService, Service, ServiceSelectDataItem } from '@assist/shared/data';

import { groupBy } from '@progress/kendo-data-query';
import { addMinutes, differenceInMinutes } from 'date-fns';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { cueClose, IconComponent } from '@cue/ui/icons';
import {
  DropdownlistComponent,
  FixedGroupTemplateDirective,
  FooterTemplateDirective,
  GroupTemplateDirective,
  DropdownsItemTemplateDirective,
} from '@cue/assist/ui/dropdowns';
import { TranslocoService } from '@jsverse/transloco';
import { LocalizedStringPipe } from '@cue/utilities';
import { localized } from '@cue/translate';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { AsyncPipe, NgStyle } from '@angular/common';
import { translation } from '@cue/translations';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'assist-service-select',
  templateUrl: './service-select.component.html',
  styleUrls: ['./service-select.component.scss'],
  imports: [
    IconComponent,
    DropdownlistComponent,
    FixedGroupTemplateDirective,
    FooterTemplateDirective,
    GroupTemplateDirective,
    DropdownsItemTemplateDirective,
    LocalizedStringPipe,
    TranslocoPipe,
    AsyncPipe,
    NgStyle,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceSelectComponent implements AfterViewInit, OnInit {
  private ordersService = inject(OrdersService);
  private translocoService = inject(TranslocoService);
  private zone = inject(NgZone);
  private configService = inject(ConfigService);
  dropdown = viewChild<DropdownlistComponent>('dropdown');

  type = input.required<'event' | 'resource'>();
  eventDate = input<Date>();

  resourceGuid = input<string>();
  resourceGuid$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  setResourceGuid = explicitEffect([this.resourceGuid], ([resourceGuid]) => {
    if (resourceGuid) {
      this.resourceGuid$.next(resourceGuid);
    }
  });

  usedServiceIds = input<any>();
  setUsedServiceIds = explicitEffect([this.usedServiceIds], ([usedServiceIds]) => {
    this.usedServiceIds$.next(usedServiceIds ?? {});
  });

  usedServiceIds$: BehaviorSubject<{ [key: number]: any }> = new BehaviorSubject({});
  filter$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  resultsData$: Observable<GenericGroupResult<ServiceSelectDataItem[]>> = null;

  itemSelected = output<ServiceSelectDataItem>();
  cancelled = output<void>();
  cueClose = cueClose;
  translation = translation;

  ngOnInit() {
    this.resultsData$ = combineLatest([
      this.filter$,
      this.resourceGuid$.pipe(switchMap((guid) => this.ordersService.getAllowedServiceIdsForResourceGuid(guid))),
      this.usedServiceIds$,
      this.resourceGuid$,
    ]).pipe(
      map(([_filter, _allowedServiceIds, _usedServiceIds, _resourceGuid]) => {
        const filteredData = this.getParsedData(
          this.configService.value.services,
          _allowedServiceIds,
          _usedServiceIds,
          _filter
        );
        const groupedData = this.getGroupedData(filteredData, this.translocoService.getActiveLang());
        return groupedData as any;
      })
    );
  }

  getParsedData(
    services: Service[],
    allowedServiceIds: number[],
    usedServiceIds: { [key: number]: any },
    search: string
  ) {
    const serviceTypes = this.configService.value.serviceTypes;
    const temp = services
      .filter((service) => {
        if (!allowedServiceIds.includes(service.id)) {
          return false;
        }
        if (!service.allowMoreItems && usedServiceIds[service.id]) {
          return false;
        }

        const serviceType = serviceTypes.find((x) => x.id === service.serviceTypeId);

        // Earliest omezeni
        if (this.eventDate() != null && serviceType.eventRequestEarliestInMinutes !== null) {
          const difference = differenceInMinutes(
            new Date(),
            addMinutes(this.eventDate(), -serviceType.eventRequestEarliestInMinutes)
          );
          if (difference < 0) {
            return false;
          }
        }

        if (
          this.eventDate() != null &&
          serviceType.eventRequestLatestInMinutes !== null &&
          serviceType.eventRequestLatestInMinutes !== 0
        ) {
          const difference = differenceInMinutes(
            new Date(),
            addMinutes(this.eventDate(), -serviceType.eventRequestLatestInMinutes)
          );
          if (difference > 0) {
            return false;
          }
        }

        if (
          this.eventDate() != null &&
          serviceType.eventRequestLatestInMinutes !== null &&
          serviceType.eventRequestLatestInMinutes == 0
        ) {
          if (new Date() > this.eventDate()) {
            return false;
          }
        }
        if (search !== undefined && search != '') {
          if (
            !localized(serviceType.name, this.translocoService).toUpperCase().includes(search.toUpperCase()) &&
            !localized(service.name, this.translocoService).toUpperCase().includes(search.toUpperCase())
          ) {
            return false;
          }
        }
        if (this.type() === 'event' && serviceType.eventRequestAllowed == false) {
          return false;
        }
        if (this.type() === 'resource' && serviceType.resourceRequestALlowed == false) {
          return false;
        }
        if (!service.available) {
          return false;
        }
        return true;
      })
      .map((service) => {
        const serviceType = serviceTypes.find((x) => x.id === service.serviceTypeId);

        let htmlName = localized(service.name, this.translocoService);

        if (search !== undefined) {
          const substringIndex = localized(service.name, this.translocoService)
            .toUpperCase()
            .indexOf(search.toUpperCase());
          if (substringIndex !== -1) {
            const firstPart = localized(service.name, this.translocoService).substr(0, substringIndex);
            const secondPart = localized(service.name, this.translocoService).substr(substringIndex, search.length);
            const thirdPart = localized(service.name, this.translocoService).substr(
              substringIndex + search.length,
              localized(service.name, this.translocoService).length - substringIndex + substringIndex
            );
            htmlName = firstPart + '<span style="font-weight: 900">' + secondPart + '</span>' + thirdPart;
          }
        }

        return {
          id: service.id,
          noteMinLength: service.noteMinLength,
          noteAllowed: service.noteAllowed,
          allowMoreItems: service.allowMoreItems,
          available: service.available,
          imageUrl: service.imageUrl,
          imagePreferCover: service.imagePreferCover,
          imageObjectPosition: service.imageObjectPosition,
          name: service.name,
          typeName: serviceType.name,
          firstInGroup: false,
          serviceTypeId: serviceType.id,
          serviceTypeName: serviceType.name,
          htmlName: htmlName,
        } as ServiceSelectDataItem;
      });

    return temp;
  }

  getGroupedData(items: ServiceSelectDataItem[], locale: string) {
    const localizedItems = items.map((item) => ({
      ...item,
      localizedTypeName: item.typeName[locale],
    }));

    return groupBy(localizedItems, [
      {
        field: 'localizedTypeName',
      },
    ]);
  }

  ngAfterViewInit() {
    this.dropdown().toggle(true);

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.dropdown().onFilterFocus();
        const popupNativeElement = (this.dropdown().kendoDropdown as any)().popupRef.popup.location.nativeElement;
        const inputElement = popupNativeElement.getElementsByTagName('input')[0];
        inputElement.focus();
      }, 1);
    });
  }

  filterChanged(value: string) {
    this.filter$.next(value);
  }

  valueChanged(value: any) {
    this.itemSelected.emit(value);
  }

  onCancel(e: MouseEvent) {
    e.stopPropagation();
    this.cancelled.emit();
  }
}
